/* What is about to happen to something you hold or follow.
 *
 * Separate from the move alerts, which are about what the market has already
 * done, and separate again from the rule engine, which is about your own
 * limits. This one is a calendar: a report due, a date that decides a dividend.
 *
 * It reports a date and says nothing about what to do with it (§1). */

import { daysUntil } from '../lib/format';
import type { EarningsDate, Watch } from '../types';
import type { FullPosition } from './derive';

export interface Reminder {
  key: string;
  label: string;
  kind: 'earnings' | 'ex-date';
  date: string;
  away: number;
  owned: boolean;
  /** A report you have written no plan for, on a position big enough to need
      one. Carried here so the card can say so rather than the user finding out
      on the day (§4.1). */
  needsPlan: boolean;
}

export function remindersDue(input: {
  positions: FullPosition[];
  watchlist: Watch[];
  earnings: EarningsDate[];
  withinDays: number;
  planOver: number;
  now?: Date;
}): Reminder[] {
  const { positions, watchlist, earnings, withinDays, planOver } = input;
  if (!(withinDays > 0)) return [];
  const now = input.now;

  const bySymbol = new Map(earnings.map((e) => [e.symbol, e]));
  const out: Reminder[] = [];

  const consider = (
    symbol: string, label: string, owned: boolean, weight: number, hasPlan: boolean,
  ) => {
    const row = bySymbol.get(symbol);
    if (!row) return;

    if (row.date) {
      const away = now ? daysUntil(row.date, now) : daysUntil(row.date);
      if (away >= 0 && away <= withinDays) {
        out.push({
          key: `e-${symbol}`, label, kind: 'earnings', date: row.date, away, owned,
          needsPlan: owned && weight > planOver && !hasPlan,
        });
      }
    }

    const ex = row.dividend?.exDate;
    if (ex) {
      const away = now ? daysUntil(ex, now) : daysUntil(ex);
      if (away >= 0 && away <= withinDays) {
        out.push({ key: `d-${symbol}`, label, kind: 'ex-date', date: ex, away, owned, needsPlan: false });
      }
    }
  };

  for (const p of positions) {
    consider(p.yahooSymbol, p.ticker, true, p.weight, !!p.thesis?.earningsPlan?.trim());
  }
  for (const w of watchlist) consider(w.yahooSymbol, w.ticker, false, 0, true);

  return out.sort((a, b) => a.away - b.away);
}

/** One line, in the same plain words the rest of the app uses (§4.2). */
export function reminderSentence(r: Reminder): string {
  const when = r.away === 0 ? 'today' : r.away === 1 ? 'tomorrow' : `in ${r.away} days`;
  return r.kind === 'earnings'
    ? `${r.label} reports ${when}.${r.needsPlan ? ' You have written no plan for it.' : ''}`
    : `${r.label} goes ex-dividend ${when}. Hold through it and the payment is yours.`;
}
